<template>
  <div class="tol-table">
    <table ref="tolTable">
      <template v-if="showSpinner">
        <tbody>
          <tr class="spinner">
            <td :colspan="columnCount"><vue-simple-spinner size="massive" /></td>
          </tr>
        </tbody>
      </template>
      <template v-else>
        <thead>
          <tr>
            <slot name="tableHeader"></slot>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, index) in tableData">
            <tr :key="'first' + index" :class="{ clickable: isRowClickable }" @click="emitRow(row)">
              <td v-if="expandable">
                <font-awesome-icon :icon="getIcon(index)" @click="onToggleExpand(index)" />
              </td>
              <slot name="check" :value="row"></slot>
              <slot name="extra" :value="row"></slot>
              <td
                v-for="column in columns"
                :key="column + row[column] + index"
                :data-label="$t(`${listingType}.listing.labels.${column}`)"
                class="table-data"
                :class="{ 'is-mobile': checkDataLabel(index, column) }"
              >
                <slot :name="column" :value="row">
                  {{ parentProperty ? row[parentProperty][column] : row[column] }}
                </slot>
                <font-awesome-icon
                  v-if="$t(`${listingType}.listing.labels.${column}`) == 'Reference Number'"
                  :icon="getIcon(index)"
                  class="show-on-mobile"
                  @click="onToggleExpand(index)"
                />
              </td>
              <slot name="click-action" :value="row"></slot>
              <td v-if="display" class="issue-btn">
                <slot name="add" :value="row"></slot>
              </td>
              <td v-if="display">
                <slot name="delete" :value="row"></slot>
              </td>
              <td v-if="display">
                <slot name="save" :value="row"></slot>
              </td>
            </tr>
            <tr v-if="expandable" :key="'second' + index">
              <td v-show="isOpen(index)" :colspan="columnCount">
                <slot name="expanded" :value="row"></slot>
              </td>
            </tr>

            <tr v-if="extraParamDisplay" :key="'third' + index">
              <td v-show="isRowOpen(row.id)" :colspan="columnCount">
                <slot name="displayExpand" :value="row"></slot>
              </td>
            </tr>
          </template>
          <template v-if="isNewRow">
            <tr>
              <slot name="emptyRow"> </slot>
            </tr>
          </template>
        </tbody>
        <tfoot>
          <tr>
            <slot name="tableFooter"></slot>
          </tr>
        </tfoot>
      </template>
    </table>
  </div>
</template>

<script>
export default {
  props: {
    tableData: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    expandable: {
      type: Boolean,
      default: false
    },
    dataLabelPrefix: {
      type: String,
      default: null
    },
    showSpinner: {
      type: Boolean,
      default: false
    },
    isRowClickable: {
      type: Boolean,
      default: false
    },
    isNewRow: {
      type: Boolean,
      default: false
    },
    display: {
      type: Boolean,
      default: false
    },
    extraParamDisplay: {
      type: Boolean,
      default: false
    },
    externalParam: {
      type: Object,
      default: null
    },
    parentProperty: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      openRows: {},
      visibleRow: {},
      isExpandedOnMobile: false,
      expandedRow: -1
    };
  },
  computed: {
    columnCount() {
      return this.$slots.tableHeader.length;
    },
    listingType() {
      const routeName = this.$route.fullPath.split('/')[1];
      let label;
      label = this.dataLabelPrefix;

      if (label) return label;

      label =
        routeName === 'time-sheets'
          ? 'timeSheets'
          : routeName === 'tes-employees'
          ? 'tesEmployees'
          : routeName;
      switch (routeName) {
        case '':
          label = 'app.homePage';
          break;
      }
      return label;
    },
    getMobileIcon() {
      return this.isExpandedOnMobile ? 'minus-square' : 'plus-square';
    }
  },

  watch: {
    externalParam: {
      immediate: true,
      handler(val) {
        if (val && val.id) {
          this.$set(this.visibleRow, val.id, val.selected);
        }
      }
    }
  },
  methods: {
    onToggleExpand(index) {
      this.$set(this.openRows, index, !this.openRows[index]);
    },
    isOpen(rowId) {
      return this.openRows[rowId];
    },
    isRowOpen(rowId) {
      return this.visibleRow[rowId];
    },
    getIcon(rowId) {
      return this.isOpen(rowId) ? 'minus-square' : 'plus-square';
    },
    checkDataLabel(rowIndex, column) {
      return (
        this.$t(`${this.listingType}.listing.labels.${column}`) !== 'Reference Number' &&
        !this.isOpen(rowIndex)
      );
    },
    emitRow(row) {
      if (this.isRowClickable) {
        this.$emit('row-click', row);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/colors.scss';
@import '../styles/variables.scss';
@import '../styles/mobile/mobile-table.scss';

@media only screen and (min-width: 769px) {
  .tol-table {
    display: flex;
    &.line-items th {
      width: 90px;
      word-wrap: break-word;
    }
    &.line-items th:first-child {
      width: 100px;
      padding: 0 0 0 50px !important;
    }
    &.profile {
      table {
        thead tr {
          background-color: $row-border;
          border: 0;
        }
        tbody tr td {
          padding: 0;
          border: 0;
        }

        tbody tr:nth-of-type(odd) {
          background-color: $background-level-3;
        }
      }
      tbody,
      thead {
        tr th:first-child,
        tr td:first-child {
          padding-left: 0;
        }
        tr th:last-child,
        tr td:last-child {
          padding-right: 0;
        }
      }
    }
    &.placement-activity {
      table {
        tbody td {
          border: 1px solid lightgrey;
          text-align: center;
        }
        thead tr {
          border: 0;
        }
        tbody tr td {
          padding: 0;
        }

        tbody tr:nth-of-type(odd) {
          background-color: $background-level-3;
        }
      }
      tbody,
      thead {
        tr th:first-child,
        tr td:first-child {
          padding-left: 0;
        }
        tr th:last-child,
        tr td:last-child {
          padding-right: 0;
        }
      }
    }
    table {
      table-layout: fixed;
      border-collapse: collapse;
      thead {
        background-color: $background-level-3;
        font-weight: bold;
        tr {
          border-top: 20px solid $background-level-3;
          border-bottom: 20px solid $background-level-3;
        }

        th {
          .perms-title {
            display: inline-block;
            padding: 0 10px;
            font-size: 0.5em;
            writing-mode: vertical-lr;
            transform: rotate(-180deg);
            text-align: center;
          }
        }
      }
      tbody {
        background-color: $background-level-2;
        border-top: 10px;
        tr td {
          padding: 20px 5px;
          border-bottom: 1pt solid $row-border;
          .remove {
            display: inline-block;
            width: 30px;
            height: 30px;

            font-size: 1.2em;
            color: $row-border;
            text-align: center;
            margin-left: auto;
            &:hover {
              cursor: pointer;
              background-color: $row-border;
              border-radius: 100%;
              color: $white;
            }
            &.history {
              display: none;
            }
          }
        }
        .table-data {
          overflow-wrap: anywhere;
        }
        tr.spinner {
          td {
            background-color: $white;
          }
        }

        tr.clickable {
          &:hover {
            background-color: $light-blue;
            cursor: pointer;
          }
        }
      }

      tbody,
      thead {
        tr th:first-child,
        tr td:first-child {
          padding-left: $details-padding;
        }
        tr th:last-child,
        tr td:last-child {
          padding-right: $details-padding;
        }
      }
    }
    &.error {
      table {
        thead {
          color: $red;
          background-color: $modified-form;
          tr {
            border-top-color: $modified-form;
            border-bottom-color: $modified-form;
          }
        }
      }
    }

    &.history {
      table {
        tr td {
          background-color: $light-grey-2;
        }
      }
    }

    &.assignment {
      table {
        font-size: 11pt;
        tbody tr td {
          padding: 20px 0;
        }
        thead tr th {
          padding-left: 0;
        }
      }
    }

    &.ctrhistory th.ctremail {
      width: 220px;
    }
    .issue-btn {
      padding-right: 10px;
    }

    &.small {
      font-size: 0.8rem;

      table {
        thead tr {
          border-top: 10px solid $background-level-3;
          border-bottom: 10px solid $background-level-3;
        }

        tbody tr td {
          padding: 5px;
        }

        tbody,
        thead {
          tr th:first-child,
          tr td:first-child {
            padding-left: $details-padding-small;
          }
          tr th:last-child,
          tr td:last-child {
            padding-right: $details-padding-small;
          }
        }
      }
    }
  }
}
</style>
